import { useEffect, useRef, useState, useMemo } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { ListNode, ListItemNode } from '@lexical/list';
import { LinkNode } from '@lexical/link';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { $generateHtmlFromNodes } from '@lexical/html';
import { $getRoot, $createParagraphNode, $createTextNode, $insertNodes } from 'lexical';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $createHeadingNode, $createQuoteNode } from '@lexical/rich-text';
import { $createListNode, $createListItemNode } from '@lexical/list';
import type { EditorState } from 'lexical';

const editorTheme = {
  paragraph: 'mb-3 leading-relaxed',
  heading: {
    h1: 'text-2xl font-bold mb-4 mt-6 text-foreground-950',
    h2: 'text-xl font-bold mb-3 mt-5 text-foreground-950',
    h3: 'text-lg font-semibold mb-2 mt-4 text-foreground-950',
  },
  list: {
    ul: 'list-disc pl-5 mb-3 space-y-1',
    ol: 'list-decimal pl-5 mb-3 space-y-1',
    listitem: 'mb-1',
  },
  link: 'text-primary-600 underline hover:text-primary-700',
  quote: 'border-l-4 border-secondary-300 pl-4 italic my-3 text-foreground-600 bg-secondary-50/50 py-2 rounded-r',
  text: {
    bold: 'font-bold',
    italic: 'italic',
    underline: 'underline',
    strikethrough: 'line-through',
  },
};

function Toolbar() {
  const [editor] = useLexicalComposerContext();
  const [activeFormats, setActiveFormats] = useState<Set<string>>(new Set());
  const [blockType, setBlockType] = useState('paragraph');

  useEffect(() => {
    const remove = editor.registerUpdateListener(() => {
      editor.getEditorState().read(() => {
        const formats = new Set<string>();
        let currentBlock = 'paragraph';

        try {
          const sel = window.getSelection();
          if (sel && sel.rangeCount > 0) {
            let node = sel.getRangeAt(0).commonAncestorContainer as HTMLElement;
            if (node.nodeType === Node.TEXT_NODE && node.parentElement) {
              node = node.parentElement;
            }

            while (node && node.tagName) {
              const tag = node.tagName.toLowerCase();
              if (tag === 'b' || tag === 'strong') formats.add('bold');
              if (tag === 'i' || tag === 'em') formats.add('italic');
              if (tag === 'u') formats.add('underline');
              if (tag === 'h1') currentBlock = 'h1';
              if (tag === 'h2') currentBlock = 'h2';
              if (tag === 'h3') currentBlock = 'h3';
              if (tag === 'ul') currentBlock = 'ul';
              if (tag === 'ol') currentBlock = 'ol';
              if (tag === 'blockquote') currentBlock = 'quote';
              node = node.parentElement as HTMLElement;
            }
          }
        } catch {
          // ignore
        }

        setActiveFormats(formats);
        setBlockType(currentBlock);
      });
    });
    return () => remove();
  }, [editor]);

  const exec = (command: string, value?: string) => {
    editor.focus();
    document.execCommand(command, false, value);
  };

  const formatBold = () => exec('bold');
  const formatItalic = () => exec('italic');
  const formatUnderline = () => exec('underline');
  const formatHeading = (level: string) => exec('formatBlock', level);
  const formatList = (type: 'ul' | 'ol') => exec(type === 'ul' ? 'insertUnorderedList' : 'insertOrderedList');
  const formatQuote = () => exec('formatBlock', 'blockquote');
  const formatLink = () => {
    const url = window.prompt('Enter URL:');
    if (url) exec('createLink', url);
  };
  const clearFormatting = () => exec('removeFormat');

  const isActive = (fmt: string) => activeFormats.has(fmt);

  const btnClass = (active: boolean) =>
    `w-8 h-8 flex items-center justify-center rounded-md text-sm transition-colors cursor-pointer ${
      active
        ? 'bg-primary-100 text-primary-700'
        : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
    }`;

  const blockBtnClass = (type: string) =>
    `px-3 py-1.5 text-xs font-medium rounded-md transition-colors cursor-pointer ${
      blockType === type
        ? 'bg-primary-100 text-primary-700'
        : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
    }`;

  return (
    <div className="flex flex-wrap items-center gap-1 p-2 bg-background-100 border-b border-background-200 rounded-t-xl">
      <div className="flex items-center gap-0.5 mr-2">
        <button type="button" onClick={formatBold} className={btnClass(isActive('bold'))} title="Bold">
          <i className="ri-bold" />
        </button>
        <button type="button" onClick={formatItalic} className={btnClass(isActive('italic'))} title="Italic">
          <i className="ri-italic" />
        </button>
        <button type="button" onClick={formatUnderline} className={btnClass(isActive('underline'))} title="Underline">
          <i className="ri-underline" />
        </button>
      </div>

      <div className="w-px h-6 bg-background-200 mx-1" />

      <div className="flex items-center gap-0.5 mr-2">
        <button type="button" onClick={() => formatHeading('h1')} className={blockBtnClass('h1')} title="Heading 1">
          H1
        </button>
        <button type="button" onClick={() => formatHeading('h2')} className={blockBtnClass('h2')} title="Heading 2">
          H2
        </button>
        <button type="button" onClick={() => formatHeading('h3')} className={blockBtnClass('h3')} title="Heading 3">
          H3
        </button>
      </div>

      <div className="w-px h-6 bg-background-200 mx-1" />

      <div className="flex items-center gap-0.5 mr-2">
        <button type="button" onClick={() => formatList('ul')} className={blockBtnClass('ul')} title="Bullet List">
          <i className="ri-list-unordered" />
        </button>
        <button type="button" onClick={() => formatList('ol')} className={blockBtnClass('ol')} title="Numbered List">
          <i className="ri-list-ordered" />
        </button>
        <button type="button" onClick={formatQuote} className={blockBtnClass('quote')} title="Quote">
          <i className="ri-double-quotes-l" />
        </button>
        <button type="button" onClick={formatLink} className={btnClass(false)} title="Link">
          <i className="ri-link" />
        </button>
      </div>

      <div className="w-px h-6 bg-background-200 mx-1" />

      <button type="button" onClick={clearFormatting} className={btnClass(false)} title="Clear Formatting">
        <i className="ri-format-clear" />
      </button>
    </div>
  );
}

interface RichTextEditorProps {
  initialValue?: string;
  onChange?: (html: string) => void;
  placeholder?: string;
  className?: string;
}

function EditorInner({ initialValue, onChange, placeholder }: RichTextEditorProps) {
  const [editor] = useLexicalComposerContext();
  const initialized = useRef(false);

  useEffect(() => {
    if (!initialValue || initialized.current) return;
    initialized.current = true;

    editor.update(() => {
      const root = $getRoot();
      root.clear();

      const wrapper = document.createElement('div');
      wrapper.innerHTML = initialValue;

      const nodes: ReturnType<typeof $createParagraphNode>[] = [];
      wrapper.childNodes.forEach((child) => {
        if (child.nodeType === Node.ELEMENT_NODE) {
          const el = child as HTMLElement;
          const tag = el.tagName.toLowerCase();

          if (tag === 'p') {
            const p = $createParagraphNode();
            p.append($createTextNode(el.textContent || ''));
            nodes.push(p);
          } else if (tag === 'h1') {
            const h = $createHeadingNode('h1');
            h.append($createTextNode(el.textContent || ''));
            nodes.push(h);
          } else if (tag === 'h2') {
            const h = $createHeadingNode('h2');
            h.append($createTextNode(el.textContent || ''));
            nodes.push(h);
          } else if (tag === 'h3') {
            const h = $createHeadingNode('h3');
            h.append($createTextNode(el.textContent || ''));
            nodes.push(h);
          } else if (tag === 'blockquote') {
            const q = $createQuoteNode();
            q.append($createTextNode(el.textContent || ''));
            nodes.push(q);
          } else if (tag === 'ul') {
            const list = $createListNode('bullet');
            el.querySelectorAll('li').forEach((li) => {
              const item = $createListItemNode();
              item.append($createTextNode(li.textContent || ''));
              list.append(item);
            });
            nodes.push(list);
          } else if (tag === 'ol') {
            const list = $createListNode('number');
            el.querySelectorAll('li').forEach((li) => {
              const item = $createListItemNode();
              item.append($createTextNode(li.textContent || ''));
              list.append(item);
            });
            nodes.push(list);
          }
        } else if (child.nodeType === Node.TEXT_NODE) {
          const p = $createParagraphNode();
          p.append($createTextNode(child.textContent || ''));
          nodes.push(p);
        }
      });

      if (nodes.length > 0) {
        $insertNodes(nodes);
      }
    });
  }, [editor, initialValue]);

  const handleChange = (editorState: EditorState) => {
    editorState.read(() => {
      const html = $generateHtmlFromNodes(editor, null);
      onChange?.(html);
    });
  };

  return (
    <>
      <RichTextPlugin
        contentEditable={
          <ContentEditable
            className="min-h-[300px] p-4 text-sm leading-relaxed text-foreground-800 outline-none empty:before:content-[attr(placeholder)] empty:before:text-foreground-400"
            placeholder={placeholder}
          />
        }
        placeholder={null}
      />
      <HistoryPlugin />
      <AutoFocusPlugin />
      <ListPlugin />
      <LinkPlugin />
      <OnChangePlugin onChange={handleChange} />
    </>
  );
}

// Static editor config — never recreated, prevents Lexical infinite re-init loop
const editorConfig = {
  namespace: 'BlogEditor',
  theme: editorTheme,
  onError: (error: Error) => {
    console.error('Lexical error:', error);
  },
  nodes: [HeadingNode, ListNode, ListItemNode, LinkNode, QuoteNode],
  editable: true,
};

export default function RichTextEditor({
  initialValue,
  onChange,
  placeholder = 'Start writing your blog post...',
  className,
}: RichTextEditorProps) {
  return (
    <div className={`border border-background-200 rounded-xl bg-white overflow-hidden ${className || ''}`}>
      <LexicalComposer initialConfig={editorConfig}>
        <div>
          <Toolbar />
          <EditorInner initialValue={initialValue} onChange={onChange} placeholder={placeholder} />
        </div>
      </LexicalComposer>
    </div>
  );
}